import { useMemo } from 'react';
import { useHasCardV2 } from 'crm-object-board-core/hooks';
import { useDraftSettings } from '../../settings/store/slice/sectionSettingsActions';
import { useCardSectionSettingsWithStore } from '../../api/useCardSectionSettings';
import { INVALID_SECTION_SETTING } from '../../api/utils';
import { useSelectedPipelineIdByAccess } from '../../hooks/useSelectedPipelineIdByAccess';
import { useDefaultCardSectionSettings } from '../../api/useDefaultCardSectionSettings';
import { CARD_ACTION, CARD_CONTENT_KEYS } from '../constants';
import { QUICK_ACTIONS_SECTION_KEY } from '../actions/container/types';
import { useCardSectionRegistry } from '../../hooks/useCardSectionRegistry';
import { useAppId } from '../../hooks/useAppId';
import { useObjectTypeId } from 'crm-object-board-core/objectTypeDefinition';
import { useIsUngatedForCPQFeature } from 'growth-payments-core/auth/gates';
import { QUOTES_SECTION_DEALS_KANBAN } from 'growth-payments-core/constants/gates';
import { QUOTES_ACTION_SECTION_KEY, QUOTES_SECTION_KEY } from '../quotes/types';

// Adapter return type

/**
 * card section settings adapter gets all the section settings and decides what should be displayed in settings UI
 * every section setting is a clickable item that opens a side panel
 * @returns an array of CardSectionSetting to be displayed in settings UI
 */
const useCardSectionSettingsAdapter = () => {
  const hasCardV2 = useHasCardV2();
  const {
    data: registry,
    loading: registryLoading
  } = useCardSectionRegistry();
  const pipelineId = useSelectedPipelineIdByAccess();
  const objectTypeId = useObjectTypeId();
  const appId = useAppId();
  // Prefetch default settings
  useDefaultCardSectionSettings();
  const {
    loading: cardSectionSettingsLoading,
    error: cardSectionSettingsError
  } = useCardSectionSettingsWithStore({
    skip: !hasCardV2,
    pipelineId,
    objectTypeId,
    appId
  });
  const loading = registryLoading || cardSectionSettingsLoading;
  const settings = useDraftSettings();
  const isUngatedForQuotesSection = useIsUngatedForCPQFeature(QUOTES_SECTION_DEALS_KANBAN);
  const contentSectionSettings = useMemo(() => settings && !loading ? settings.filter(s => CARD_CONTENT_KEYS.some(x => x === s.sectionKey)).filter(s => s.sectionKey !== QUOTES_SECTION_KEY || isUngatedForQuotesSection).map(sectionSettings => {
    return {
      sectionKey: sectionSettings.sectionKey,
      settings: sectionSettings.settings,
      Component: registry.getSectionSettingComponent(sectionSettings.sectionKey)
    };
  })
  // filter out any section that have invalid settings, BE returns {} for valid empty setting cases.
  .filter(c => c.settings !== INVALID_SECTION_SETTING)
  // filter out any sections that don't have a component
  .filter(c => c.Component) : [], [settings, registry, loading, isUngatedForQuotesSection]);
  const sectionSettingConfigs = useMemo(() => {
    if (registryLoading) {
      return undefined;
    }
    const sectionSettingConfigsFromRegistry = registry.getAllSectionSettingConfigs();
    if (!sectionSettingConfigsFromRegistry) {
      return undefined;
    }
    if (!isUngatedForQuotesSection) {
      sectionSettingConfigsFromRegistry.delete(QUOTES_SECTION_KEY);
      sectionSettingConfigsFromRegistry.delete(QUOTES_ACTION_SECTION_KEY);
    }
    return sectionSettingConfigsFromRegistry;
  }, [registry, registryLoading, isUngatedForQuotesSection]);
  const actionSectionKeys = useMemo(() => !registryLoading && hasCardV2 ? registry.getSectionKeys().filter(sectionKey => {
    return registry.getSectionType(sectionKey) === CARD_ACTION && (sectionKey !== QUOTES_ACTION_SECTION_KEY || isUngatedForQuotesSection);
  }).map(sectionKey => {
    return sectionKey;
  }) : [], [registry, registryLoading, hasCardV2, isUngatedForQuotesSection]);
  const quickActionSettings = useMemo(() => {
    const foundSettings = settings === null || settings === void 0 ? void 0 : settings.find(x => x.sectionKey === QUICK_ACTIONS_SECTION_KEY);
    if (!foundSettings) {
      return undefined;
    }
    const filteredQuickActions = isUngatedForQuotesSection ? foundSettings === null || foundSettings === void 0 ? void 0 : foundSettings.settings.quickActions : foundSettings === null || foundSettings === void 0 ? void 0 : foundSettings.settings.quickActions.filter(quickAction => quickAction.type !== QUOTES_ACTION_SECTION_KEY);
    return Object.assign({}, foundSettings, {
      settings: Object.assign({}, foundSettings === null || foundSettings === void 0 ? void 0 : foundSettings.settings, {
        quickActions: filteredQuickActions
      })
    });
  }, [settings, isUngatedForQuotesSection]);
  return {
    contentSectionSettings,
    sectionSettingConfigs,
    quickActionSettings,
    actionSectionKeys,
    cardSectionSettingsLoading,
    cardSectionSettingsError
  };
};
export default useCardSectionSettingsAdapter;